import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import FormProvider, { RHFSelect, RHFInput, RHFCheckbox } from 'Components/Organisms/HookForm';
import Button from 'Components/Atoms/Button/Button';
import useCountries from 'Hooks/useCountries';
import { INVOICE_SUBTYPES } from 'Utils/constants';
import AlertForm from 'Components/Molecules/AlertForm/AlertForm';
import Divider from 'Components/Atoms/Divider/Divider';

const getInvoiceDocTypes = (t) => [
    { value: null, label: t('tax-authority-config-all-option') },
    { value: 'F1', label: t('verifactu-invoice-type-f1') },
    { value: 'F2', label: t('verifactu-invoice-type-f2') },
    { value: 'F3', label: t('verifactu-invoice-type-f3') },
    { value: 'R1', label: t('verifactu-invoice-type-r1') },
    { value: 'R2', label: t('verifactu-invoice-type-r2') },
    { value: 'R3', label: t('verifactu-invoice-type-r3') },
    { value: 'R4', label: t('verifactu-invoice-type-r4') },
    { value: 'R5', label: t('verifactu-invoice-type-r5') },
];

const getIssuerTypeOptions = (t) => [
    { value: null, label: t('verifactu-issuer-type-direct') },
    { value: 'T', label: t('verifactu-issuer-type-third-party') },
    { value: 'D', label: t('verifactu-issuer-type-self-invoice') },
];

const getRegimeOptions = (t) => [
    { value: '01', label: t('verifactu-vat-regime-01') },
    { value: '02', label: t('verifactu-vat-regime-02') },
    { value: '03', label: t('verifactu-vat-regime-03') },
    { value: '04', label: t('verifactu-vat-regime-04') },
    { value: '05', label: t('verifactu-vat-regime-05') },
    { value: '06', label: t('verifactu-vat-regime-06') },
    { value: '07', label: t('verifactu-vat-regime-07') },
    { value: '08', label: t('verifactu-vat-regime-08') },
    { value: '09', label: t('verifactu-vat-regime-09') },
    { value: '10', label: t('verifactu-vat-regime-10') },
    { value: '11', label: t('verifactu-vat-regime-11') },
    { value: '14', label: t('verifactu-vat-regime-14') },
    { value: '15', label: t('verifactu-vat-regime-15') },
    { value: '17', label: t('verifactu-vat-regime-17') },
    { value: '18', label: t('verifactu-vat-regime-18') },
    { value: '19', label: t('verifactu-vat-regime-19') },
    { value: '20', label: t('verifactu-vat-regime-20') },
];

export default function InvoicesTaxAuthorityConfigVerifactuForm({
    defaultValues,
    onSave,
    onClose,
    onSuccess,
    onError,
}) {
    const { t } = useTranslation('invoices');
    const { t: tContacts } = useTranslation('contacts');
    const { countries } = useCountries();
    const [conflictError, setConflictError] = useState(false);

    const countriesOptions = useMemo(
        () => [
            { value: null, label: t('tax-authority-config-all-option') },
            ...(countries ?? [])
                .map((c) => ({ value: c.code, label: c.name }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        ],
        [countries, t],
    );

    const invoiceSubtypeOptions = useMemo(
        () => [
            { value: null, label: t('tax-authority-config-all-option') },
            { value: INVOICE_SUBTYPES.STANDARD, label: t('invoice-standard') },
            { value: INVOICE_SUBTYPES.STANDARD_CORRECTIVE, label: t('invoice-standard-corrective') },
            { value: INVOICE_SUBTYPES.SIMPLIFIED, label: t('invoice-simplified') },
            { value: INVOICE_SUBTYPES.SIMPLIFIED_CORRECTIVE, label: t('invoice-simplified-corrective') },
        ],
        [t],
    );

    const contactTypeOptions = useMemo(
        () => [
            { value: null, label: t('tax-authority-config-all-option') },
            { value: 'company', label: tContacts('company') },
            { value: 'freelancer', label: tContacts('freelancer') },
            { value: 'individual', label: tContacts('individual') },
        ],
        [t, tContacts],
    );

    const schema = yup.object().shape({
        country: yup.string().nullable(),
        es_verifactu_regime: yup.string().required(t('field-required')),
        representative_name: yup
            .string()
            .nullable()
            .when('es_verifactu_issuer_type', {
                is: (v) => v != null,
                then: (s) => s.required(t('field-required')),
            }),
        representative_tax_id: yup
            .string()
            .nullable()
            .when('es_verifactu_issuer_type', {
                is: (v) => v != null,
                then: (s) => s.required(t('field-required')),
            }),
        representative_country: yup
            .string()
            .nullable()
            .when('es_verifactu_issuer_type', {
                is: (v) => v != null,
                then: (s) => s.required(t('field-required')),
            }),
    });

    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues,
    });

    const {
        handleSubmit,
        watch,
        formState: { isSubmitting },
    } = methods;

    const issuerType = watch('es_verifactu_issuer_type');
    const showRepresentative = issuerType === 'T';

    const submit = async (data) => {
        try {
            await onSave({
                country: data.country,
                invoice_subtype: data.invoice_subtype,
                contact_type: data.contact_type,
                es_verifactu_doc_type: data.es_verifactu_doc_type,
                es_verifactu_simplified_art7273: data.simplified_art7273_bool ? 'S' : 'N',
                es_verifactu_issuer_type: data.es_verifactu_issuer_type,
                representative_name: showRepresentative ? data.representative_name : null,
                representative_tax_id: showRepresentative ? data.representative_tax_id : null,
                representative_country: showRepresentative ? data.representative_country : null,
                es_verifactu_regime: data.es_verifactu_regime,
                contact_id: null,
            });
            onSuccess();
        } catch (error) {
            if (error.response?.status === 409) {
                setConflictError(true);
            } else {
                onError(error);
            }
        }
    };

    return (
        <FormProvider className="flex flex-col h-full" methods={methods} onSubmit={handleSubmit(submit)}>
            <div className="grow py-6">
                <div className="grid gap-5">
                    {conflictError && (
                        <AlertForm className="mb-3" onClose={() => setConflictError(false)}>
                            {t('tax-authority-config-duplicate-error')}
                        </AlertForm>
                    )}
                    <RHFSelect
                        className="w-full"
                        name="country"
                        label={t('tax-authority-config-country-label')}
                        options={countriesOptions}
                        data-autofocus="true"
                        enableFilter
                    />
                    <RHFSelect
                        className="w-full"
                        name="invoice_subtype"
                        label={t('tax-authority-config-invoice-subtype-label')}
                        options={invoiceSubtypeOptions}
                    />
                    <RHFSelect
                        className="w-full"
                        name="contact_type"
                        label={t('tax-authority-config-contact-type-label')}
                        options={contactTypeOptions}
                    />

                    <Divider />

                    <RHFSelect
                        className="w-full"
                        name="es_verifactu_doc_type"
                        label={t('verifactu-invoice-type-label')}
                        options={getInvoiceDocTypes(t)}
                    />
                    <RHFSelect
                        className="w-full"
                        name="es_verifactu_regime"
                        label={t('tax-authority-config-regime-label')}
                        options={getRegimeOptions(t)}
                    />
                    <RHFSelect
                        className="w-full"
                        name="es_verifactu_issuer_type"
                        label={t('verifactu-issuer-type-label')}
                        options={getIssuerTypeOptions(t)}
                    />
                    {showRepresentative && (
                        <>
                            <RHFInput
                                name="representative_name"
                                label={t('verifactu-representative-name-label')}
                                placeholder={t('verifactu-representative-name-placeholder')}
                            />
                            <RHFInput
                                name="representative_tax_id"
                                label={t('verifactu-representative-tax-id-label')}
                                placeholder={t('verifactu-representative-tax-id-placeholder')}
                            />
                            <RHFSelect
                                className="w-full"
                                name="representative_country"
                                label={t('verifactu-representative-country-label')}
                                options={countriesOptions}
                                enableFilter
                            />
                        </>
                    )}
                    <RHFCheckbox name="simplified_art7273_bool">
                        <span className="text-sm">{t('verifactu-simplified-with-client-data-label')}</span>
                    </RHFCheckbox>
                </div>
            </div>
            <div className="sticky bottom-0 bg-white pt-2 pb-4 z-10">
                <div className="flex gap-2">
                    <Button variant="primary" isBusy={isSubmitting} type="submit" className="grow">
                        {t('save')}
                    </Button>
                    <Button onClick={onClose} className="grow">
                        {t('cancel')}
                    </Button>
                </div>
            </div>
        </FormProvider>
    );
}

InvoicesTaxAuthorityConfigVerifactuForm.propTypes = {
    defaultValues: PropTypes.object.isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
};
